import React from 'react'
import { irwin } from 'config'
import useIrmAffiliations from '../hooks/useIrmAffiliations'
import useSearchInputRead from '../hooks/useSearchInputRead'
import { Grid } from '@the-platform-group/component-library/Layout'
import { CustomLink as Link, Typography } from '@the-platform-group/component-library'
import { useFileDownload } from 'hooks/useFileDownload'
import SharedTable from './SharedTable'
import { ExportButton } from '@the-platform-group/component-library/Button'
import { getIrmAffiliationsCSVUrl } from 'services/irmAffiliations'

export const emptyMessage =
  "Please enter an affiliated ID for a system person or organization. If the affiliated entity is a custom person or organization, please go to person's profile in that customer's account."

// Some ledger source types (custom field values, key contacts, and orphaned
// "unknown" rows) have no linkable profile, so buildUrlAndContent returns an
// empty url. Render those as plain text rather than an anchor pointing at an
// empty path, which would otherwise open a new tab to the current location.
const AffiliationCell = React.memo(({ value }) => {
  if (value.url) {
    return (
      <Link to={{ pathname: value.url }} target="_blank">
        {value.content}
      </Link>
    )
  }

  return <Typography>{value.content}</Typography>
})

const columns = [
  { field: 'customer', flex: 0.25 },
  {
    field: 'affiliation',
    renderCell: params => <AffiliationCell {...params} />,
  },
]

export const buildUrlAndContent = (source, affiliatedType) => {
  const { id, type } = source || { id: 'unknown', type: 'unknown' }
  switch (type) {
    case 'activity':
      return {
        url: `${irwin.appUrl}/activities/${id}`,
        content: `${source.type} (${source.date}) - ${source.title}`,
      }
    case 'tag':
      return {
        url:
          affiliatedType === 'person'
            ? `${irwin.appUrl}/irm/people?tags=${id}`
            : `${irwin.appUrl}/irm/organizations?tags=${id}`,
        content: `${source.type} - ${source.name}`,
      }
    case 'customContact':
      return {
        url: `${irwin.appUrl}/people/${id}`,
        content: `Custom Contact - ${source.firstName} ${source.lastName}`,
      }
    case 'customInstitution':
      return {
        url: `${irwin.appUrl}/organizations/${id}`,
        content: `Custom Institution - ${source.name}`,
      }
    case 'customFieldValue':
      return {
        url: '',
        content: `Custom Field - ${source.name}`,
      }
    case 'customerFavoritePerson':
      // The source is the favorite (star) record, not the person/org, and the
      // response carries no affiliated_id to build a profile link from — so show
      // the key contact's name without a link (as with customFieldValue).
      return {
        url: '',
        content: `Key Contact - ${source.name}`,
      }
    case 'unknown':
      return {
        url: '',
        content: 'Unknown (deleted affiliation)',
      }
    default:
      throw new Error(`Unsupported source type: ${type}`)
  }
}

const getRowData = affiliation => {
  const { id, customer, source, affiliatedType } = affiliation
  return {
    id,
    customer: customer?.name,
    affiliation: buildUrlAndContent(source, affiliatedType),
  }
}

function IrmAffiliationsTable() {
  const { affiliatedId } = useSearchInputRead()
  const irmAffiliation = useIrmAffiliations(affiliatedId)
  const props = {
    columns,
    emptyMessage,
    getRowData,
    useHook: irmAffiliation,
    name: 'irmAffiliationsTable',
  }
  const { download, isLoading: isDownloading } = useFileDownload()

  const handleExportCSV = () => {
    const csvUrl = getIrmAffiliationsCSVUrl(affiliatedId)
    download(csvUrl)
  }

  return (
    <>
      {affiliatedId && (
        <Grid item container xs={12} justifyContent="flex-end">
          <Grid item>
            <ExportButton
              isLoading={isDownloading}
              handleClick={handleExportCSV}
              disabled={
                irmAffiliation.state.isLoading ||
                irmAffiliation.state.failed ||
                !irmAffiliation.state.total
              }
              options={[
                {
                  onClick: handleExportCSV,
                },
              ]}
            />
          </Grid>
        </Grid>
      )}
      <SharedTable rows={irmAffiliation.state.affiliations} {...props} />
    </>
  )
}

export default IrmAffiliationsTable
